import { ARTICLE_EVIDENCE_MODULES } from './articleEvidenceModules.js';
import { appendFaqSection } from './articleFaq.js';
import { buildEnrichmentHtml, buildUpdateNoteHtml, getArticleEnrichment } from './articleEnrichment.js';
import { getArticleQueryFaqs } from './articleQueryFaqs.js';

export const ARTICLE_EDITORIAL_POLICY_VERSION = 'bluebear.editorial-policy.v1';

const terminologyReplacements = [
  [
    /its MCP gateway \(Management and Control Plane gateway\)/gi,
    'the BlueBear MCP Gateway—its governance layer around Model Context Protocol connections—',
  ],
  [
    /The Multi-Cloud Provider \(MCP\) gateway enables consistent management across diverse cloud environments, preventing vendor lock-in and optimizing infrastructure costs\./gi,
    'The BlueBear MCP Gateway governs Model Context Protocol connections, credentials, policy checks, and audit evidence across agent environments. It does not redefine MCP as a cloud-management acronym.',
  ],
  [
    /the MCP \(Multi-Cloud Platform\) Gateway/gi,
    'the BlueBear MCP Gateway—a governance and control layer around Model Context Protocol connections—',
  ],
];

const routingSpecification = `<h2>BlueBear Model Routing Policy Specification</h2>

<p>The current BlueBear frontend contract makes routing policy concrete rather than aspirational. The fields below are taken from the policy payload assembled by <code>buildModelRoutingUpdatePayload</code>; they are implementation fields, not proposed marketing terminology.</p>

<table>
  <thead><tr><th>Policy field</th><th>Operational decision</th><th>Evidence to preserve</th></tr></thead>
  <tbody>
    <tr><td><code>mode</code></td><td>Whether optimized routing is off, available, or enforced for the workflow.</td><td>The effective mode used for the request.</td></tr>
    <tr><td><code>optimizationGoal</code></td><td>The declared objective, such as a balanced cost-and-performance policy.</td><td>The goal in force when the model was selected.</td></tr>
    <tr><td><code>allowedBackends</code> and <code>blockedModels</code></td><td>The eligible provider and model set before scoring begins.</td><td>Eligible candidates and exclusion reasons.</td></tr>
    <tr><td><code>dataResidency</code> and <code>complianceClass</code></td><td>The location and compliance boundary the request must satisfy.</td><td>The boundary evaluated for the selected backend.</td></tr>
    <tr><td><code>latencyTier</code> and <code>constraints.maxLatencyMs</code></td><td>The latency class and hard response-time ceiling.</td><td>Target and observed latency.</td></tr>
    <tr><td><code>costTier</code> and <code>constraints.maxCostUsd</code></td><td>The cost class and maximum permitted request cost.</td><td>Budget ceiling and estimated or observed cost.</td></tr>
    <tr><td><code>constraints.minConfidence</code></td><td>The minimum acceptable confidence before completion or fallback.</td><td>Confidence threshold and evaluation result.</td></tr>
    <tr><td><code>fallbackPolicy</code> and <code>auditMode</code></td><td>What happens when the preferred route fails and how much evidence is retained.</td><td>Fallback trigger, decision, selected route, and audit mode.</td></tr>
    <tr><td><code>userControls.allowOptimizedRouting</code></td><td>Whether a user may opt into optimized routing when platform policy permits it.</td><td>The user control and platform permission in force.</td></tr>
  </tbody>
</table>

<h3>Machine-readable example</h3>
<p>This illustrative payload uses the real BlueBear field names. The values are an example for policy review; they are not presented as production measurements.</p>
<pre><code>{
  "mode": "enforced",
  "optimizationGoal": "balanced",
  "dataResidency": "us",
  "allowedBackends": ["approved-primary", "approved-fallback"],
  "blockedModels": ["unapproved-model"],
  "complianceClass": "internal",
  "latencyTier": "interactive",
  "costTier": "standard",
  "fallbackPolicy": "approved-backends-only",
  "auditMode": "full",
  "constraints": { "maxLatencyMs": 3000, "maxCostUsd": 0.08, "minConfidence": 0.85 },
  "userControls": { "allowOptimizedRouting": false }
}</code></pre>

<h3>Evaluation order</h3>
<ol>
  <li>Reject models outside the allowed backend, blocked-model, residency, or compliance boundary.</li>
  <li>Apply latency, cost, and confidence constraints to the remaining candidates.</li>
  <li>Rank eligible candidates against the declared optimization goal.</li>
  <li>Execute the selected route and preserve the effective policy with the request evidence.</li>
  <li>Apply fallback only when the declared fallback policy allows it; otherwise stop and surface the failure.</li>
</ol>
<p>This separation matters: eligibility is a governance decision, ranking is an optimization decision, and fallback is an exception-handling decision. Combining them into one opaque score makes incident reconstruction and policy review harder.</p>`;

const ownershipMatrix = `<h2>BlueBear AI Agent Ownership Matrix</h2>

<p>This operating template maps each production decision to one accountable owner and the evidence BlueBear should preserve. It is an implementation guide, not a claim about a particular customer organization.</p>

<table>
  <thead><tr><th>Decision</th><th>Accountable owner</th><th>Responsible operator</th><th>Required evidence</th></tr></thead>
  <tbody>
    <tr><td>Approve the workflow and acceptable outcome</td><td>Business workflow owner</td><td>Product or operations lead</td><td>Workflow version, acceptance criteria, risk tier</td></tr>
    <tr><td>Approve models, routing, and fallback</td><td>AI platform owner</td><td>Platform engineering</td><td>Effective routing policy, eligible models, fallback decision</td></tr>
    <tr><td>Approve tools, credentials, and scopes</td><td>Security owner</td><td>Integration owner</td><td>Connection ID, scopes, policy evaluation, credential reference</td></tr>
    <tr><td>Operate runtime isolation and capacity</td><td>Platform engineering lead</td><td>Runtime operations</td><td>Workspace, tenant boundary, runtime version, quota state</td></tr>
    <tr><td>Review exceptions and human approvals</td><td>Business workflow owner</td><td>Named review queue</td><td>Exception reason, reviewer, decision, elapsed time</td></tr>
    <tr><td>Declare and coordinate an incident</td><td>Service owner</td><td>Incident commander</td><td>Session IDs, timeline, affected tools/models, containment action</td></tr>
    <tr><td>Attribute cost and approve continued operation</td><td>Business sponsor</td><td>FinOps or operations analyst</td><td>Cost per accepted outcome, retry cost, review cost, budget decision</td></tr>
    <tr><td>Retire the agent and revoke access</td><td>Service owner</td><td>Platform and integration operations</td><td>Retirement approval, disabled routes, revoked connections, retention state</td></tr>
  </tbody>
</table>

<h3>Escalation rule</h3>
<p>Do not use a committee as the accountable owner. If a row has no named accountable role, the agent is not ready for production. BlueBear can preserve the workspace, routing, integration, session, approval, and cost evidence, but the organization must assign the human decision rights.</p>`;

const modules = {
  ...ARTICLE_EVIDENCE_MODULES,
  'ai-model-routing-policy-guide': {
    heading: 'BlueBear Model Routing Policy Specification',
    before: '<h2>Diagnostic Checklist for Your Current AI Model Routing</h2>',
    html: routingSpecification,
  },
  'ai-agent-operating-model-raci': {
    heading: 'BlueBear AI Agent Ownership Matrix',
    before: '<h2>Practical Diagnostic Checklist',
    html: ownershipMatrix,
  },
};

const searchConsoleUpgrades = {
  'ai-agent-control-plane-explained': {
    heading: 'Direct Answer: What an AI Agent Control Plane Controls',
    before: '<h2>Next Steps',
    citations: [
      'https://airc.nist.gov/airmf-resources/airmf/5-sec-core/',
      'https://csrc.nist.gov/pubs/other/2026/02/05/accelerating-the-adoption-of-software-and-ai-agent/ipd',
    ],
    html: `<h2>Direct Answer: What an AI Agent Control Plane Controls</h2>
<p><strong>An AI agent control plane is the governance layer that decides which agent may run, on whose behalf, with which models, tools, data, budgets, approvals, and evidence requirements.</strong> The runtime plane executes that decision; the data plane carries prompts, retrieved context, tool payloads, and results.</p>
<table><thead><tr><th>Requirement</th><th>Control-plane decision</th><th>Proof to inspect</th></tr></thead><tbody>
<tr><td>Identity</td><td>Human, workload, tenant, workspace, and delegation.</td><td>Effective subject and delegated authority.</td></tr>
<tr><td>Capability</td><td>Allowed model, connection, tool, action, and resource.</td><td>Policy version, decision, reason, and denial test.</td></tr>
<tr><td>Human control</td><td>Which action needs which approver and assurance.</td><td>Bound approval, expiry, and execution receipt.</td></tr>
<tr><td>Economics</td><td>Budget, route, attempt, and concurrency ceilings.</td><td>Cost per accepted outcome and budget-stop event.</td></tr>
<tr><td>Lifecycle</td><td>Version, release, exception, revocation, and retirement.</td><td>Owner, change record, active scope, and final state.</td></tr>
</tbody></table>
<p>A catalog or dashboard is not a control plane unless it can prevent an unauthorized action at an enforcement boundary the model cannot rewrite.</p>`,
  },
  'ai-agent-observability-opentelemetry': {
    heading: '2026 OpenTelemetry Field Map for Agents and Tools',
    before: '<h2>Practical Diagnostic Checklist',
    citations: ['https://opentelemetry.io/docs/specs/semconv/registry/attributes/gen-ai/'],
    html: `<h2>2026 OpenTelemetry Field Map for Agents and Tools</h2>
<p>OpenTelemetry's current GenAI registry includes well-known operations such as <code>invoke_agent</code>, <code>invoke_workflow</code>, and <code>execute_tool</code>. Treat the conventions as a shared vocabulary, then add stable business identifiers for tenant, workspace, workflow version, policy, approval, and accepted outcome.</p>
<table><thead><tr><th>Operation</th><th>Record</th><th>Privacy rule</th></tr></thead><tbody>
<tr><td>Agent/workflow invocation</td><td><code>gen_ai.operation.name</code>, provider, request model, response model, session and workflow.</td><td>Do not put personal or secret data in low-cardinality resource attributes.</td></tr>
<tr><td>Model call</td><td>model, token usage, finish reason, latency, attempt and route.</td><td>Prompt and output content are opt-in and separately retained.</td></tr>
<tr><td>Tool execution</td><td>tool name/type, call ID, status, latency and correlated request receipt.</td><td>Tool arguments and results may contain sensitive information; redact or omit by policy.</td></tr>
<tr><td>Approval</td><td>action fingerprint, requirement, reviewer, decision, expiry and reason.</td><td>Store the minimum reviewer identity needed for accountability.</td></tr>
<tr><td>Outcome</td><td>terminal state, downstream identifier, acceptance, retry and cost.</td><td>Link to the system of record instead of duplicating sensitive payloads.</td></tr>
</tbody></table>
<p>Test collector loss explicitly. If required evidence cannot be preserved, high-impact execution should fail closed or enter a documented degraded mode.</p>`,
  },
  'human-in-the-loop-ai-agent-approvals': {
    heading: 'When to Use CIBA-Style Decoupled Approval',
    before: '<h2>Practical Diagnostic Checklist',
    citations: ['https://www.rfc-editor.org/rfc/rfc9101'],
    html: `<h2>When to Use CIBA-Style Decoupled Approval</h2>
<p>OpenID Connect Client-Initiated Backchannel Authentication (CIBA) fits an agent workflow when the approver authenticates on a separate device or channel and the work can wait asynchronously. It authenticates and communicates a decision; it does not define the business policy or prove that an ambiguous action was understood.</p>
<table><thead><tr><th>Approval pattern</th><th>Use when</th><th>Required binding</th></tr></thead><tbody>
<tr><td>Inline confirmation</td><td>The requester is present and the action is low-to-moderate risk.</td><td>Current session, exact action, resource, material parameters.</td></tr>
<tr><td>CIBA-style backchannel</td><td>A different approver or trusted authentication device is required.</td><td>Action fingerprint, approver authority, authentication context, expiry, one-time use.</td></tr>
<tr><td>Dual control</td><td>Policy requires separation of duties or two approvers.</td><td>Independent identities and decisions over the same immutable fingerprint.</td></tr>
</tbody></table>
<p>If destination, data class, resource, tool, amount, or other material parameter changes, invalidate the approval and request a new decision.</p>`,
  },
  'ai-agent-audit-trail-compliance': {
    heading: 'Audit Trail Requirements for Every Consequential Agent Action',
    before: '<h2>Practical Diagnostic Checklist',
    citations: [
      'https://csrc.nist.gov/pubs/other/2026/02/05/accelerating-the-adoption-of-software-and-ai-agent/ipd',
      'https://opentelemetry.io/docs/specs/semconv/registry/attributes/gen-ai/',
    ],
    html: `<h2>Audit Trail Requirements for Every Consequential Agent Action</h2>
<p>A compliant-looking log is not enough. A reviewer should be able to reconstruct identity, delegated authority, policy, proposal, approval, execution, side effect, and accepted outcome without inferring missing joins.</p>
<table><thead><tr><th>Evidence group</th><th>Minimum fields</th></tr></thead><tbody>
<tr><td>Context</td><td>Tenant, workspace, human/service subject, agent and workflow version, session.</td></tr>
<tr><td>Authorization</td><td>Requested action/resource, effective policy, decision, reason, approval and expiry.</td></tr>
<tr><td>Execution</td><td>Model route, MCP connection, tool/action, credential reference, attempt and result.</td></tr>
<tr><td>Side effect</td><td>Idempotency key, downstream receipt, before/after reference and reconciliation state.</td></tr>
<tr><td>Outcome</td><td>Terminal state, accepted result, rejection, retry, review effort and cost.</td></tr>
</tbody></table>
<p>Protect integrity with append-only corrections, stable time sources, ingestion-gap monitoring, exportable schemas, content-level privacy controls, and retention by data class and jurisdiction.</p>`,
  },
  'ai-agent-incident-response-runbook': {
    heading: 'Map the Runbook to NIST SP 800-61r3',
    before: '<h2>Practical Diagnostic Checklist',
    citations: ['https://nvlpubs.nist.gov/nistpubs/specialpublications/nist.sp.800-61r3.pdf'],
    html: `<h2>Map the Runbook to NIST SP 800-61r3</h2>
<p>NIST SP 800-61r3 supersedes Revision 2 and treats incident response as part of Cybersecurity Framework 2.0 risk management. For agents, prepare and govern the inventory and authority model; identify and protect identities, tools, data, and evidence; detect abnormal plans and actions; respond by containing the narrowest capability; and recover only after replay, reconciliation, and owner approval.</p>
<table><thead><tr><th>Agent incident</th><th>First containment</th><th>Preserve</th></tr></thead><tbody>
<tr><td>Identity/credential compromise</td><td>Revoke workload and connector credentials.</td><td>Issuer, audience, scope and affected sessions.</td></tr>
<tr><td>Prompt injection/intent drift</td><td>Disable affected source or tool path; freeze version.</td><td>Source provenance, plan, policy and tool decisions.</td></tr>
<tr><td>Unsafe or duplicate side effect</td><td>Disable execute scope and retries.</td><td>Approval, idempotency key, tool receipt and downstream state.</td></tr>
<tr><td>Evidence loss</td><td>Fail high-impact actions closed or enter approved degraded mode.</td><td>Last sequence, missing interval and collector health.</td></tr>
</tbody></table>`,
  },
  'ai-agent-reliability-evidence-chain': {
    heading: 'Reliability SLOs That Include the Business Outcome',
    before: '<h2>Practical Diagnostic Checklist',
    citations: ['https://sre.google/sre-book/service-level-objectives/'],
    html: `<h2>Reliability SLOs That Include the Business Outcome</h2>
<p><strong>Agent reliability is the probability that a versioned workflow produces an accepted outcome inside its authority and safety boundaries, within time and cost objectives.</strong> HTTP availability is necessary but insufficient.</p>
<table><thead><tr><th>Indicator</th><th>Calculation</th></tr></thead><tbody>
<tr><td>Accepted outcome rate</td><td>Business-accepted outcomes / initiated workflows.</td></tr>
<tr><td>Unsafe action rate</td><td>Executed actions outside policy or approval; this should be a stop condition.</td></tr>
<tr><td>Time to accepted outcome</td><td>Elapsed time including queue, retries, approvals and repair.</td></tr>
<tr><td>Recovery success</td><td>Failed runs reconciled without duplicate side effects / recoverable failures.</td></tr>
<tr><td>Evidence completeness</td><td>Runs with required identity, policy, tool, approval and outcome records / runs.</td></tr>
</tbody></table>
<p>Segment these indicators by workflow version, model route, tool, tenant and risk tier; aggregate averages hide regressions.</p>`,
  },
};

function insertModule(contentHtml, module) {
  if (!module || contentHtml.includes(`<h2>${module.heading}</h2>`)) return contentHtml;
  const index = contentHtml.indexOf(module.before);
  if (index < 0) return `${contentHtml}\n\n${module.html}`;
  return `${contentHtml.slice(0, index)}${module.html}\n\n${contentHtml.slice(index)}`;
}

function headingText(value) {
  return String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function normalizeArticleBodyHeadings(contentHtml, title) {
  const articleTitle = headingText(title);
  return String(contentHtml || '').replace(/<h1\b([^>]*)>([\s\S]*?)<\/h1>/gi, (_match, attributes, innerHtml) => {
    if (headingText(innerHtml) === articleTitle) return '';
    return `<h2${attributes}>${innerHtml}</h2>`;
  });
}

export function applyArticleEditorialPolicy(post) {
  let contentHtml = normalizeArticleBodyHeadings(post.contentHtml, post.title);
  for (const [pattern, replacement] of terminologyReplacements) {
    contentHtml = contentHtml.replace(pattern, replacement);
  }
  contentHtml = insertModule(contentHtml, modules[post.slug]);
  contentHtml = insertModule(contentHtml, searchConsoleUpgrades[post.slug]);
  // Additive improvements to articles that already rank: a dated statement of what changed at the
  // top, new evidence and internal links at the bottom, and the original prose untouched between them.
  const enrichment = getArticleEnrichment(post.slug);
  if (enrichment && !contentHtml.includes('article-update-note')) {
    const note = buildUpdateNoteHtml(enrichment);
    const appended = buildEnrichmentHtml(enrichment);
    if (note) contentHtml = `${note}\n\n${contentHtml}`;
    if (appended) contentHtml = `${contentHtml}\n\n${appended}`;
  }

  // Visible Q&A, appended from the same source the FAQ schema is built from, so the two can never
  // drift. Google's structured-data policy forbids marking up content that is not on the page.
  const faqs = post.faqs || enrichment?.faqs || getArticleQueryFaqs(post.slug);
  contentHtml = appendFaqSection(contentHtml, faqs);
  const moduleCitations = [
    ...(modules[post.slug]?.citations || []),
    ...(searchConsoleUpgrades[post.slug]?.citations || []),
  ];
  return {
    ...post,
    contentHtml,
    ...(faqs.length ? { faqs } : {}),
    // dateModified is only ever set alongside a real change, and the change is stated in the article.
    ...(enrichment?.dateModified ? { dateModified: enrichment.dateModified } : {}),
    sourceUrls: [...new Set([...(post.sourceUrls || []), ...moduleCitations, ...(enrichment?.sourceUrls || [])])],
    editorialPolicyVersion: ARTICLE_EDITORIAL_POLICY_VERSION,
  };
}

export function applyArticleEditorialPolicyToCorpus(posts) {
  return posts.map(applyArticleEditorialPolicy);
}
